<template>
    <div>
        <application-title
            :title="$t('applications.personal_info')"
        >
            <application-button
                :condition="canManage"
                :title="$t('general.edit_subject', { subject: this.$t('applications.applicant')})"
                icon="edit"
                @click="$emit('edit')"
            />
        </application-title>

        <div class="w-full grid gap-12 grid-cols-2">
            <div v-for="entry in personalData"
                 :key="'entry_' + entry.name + entry.id"
                 class="flex relative"
                 @mouseleave="actionButtonsInfo = null"
                 @mouseover="actionButtonsInfo = entry.name + entry.id"
            >
                <div class="w-full">
                    <div class="flex items-center">
                        <click-edit-input
                            v-if="entry.id"
                            class="block font-semibold mb-4 first-letter:uppercase w-full"
                            classes="font-semibold text-black"
                            :disabled="!canManage"
                            :full-width="true"
                            :placeholder="$t('jobs.title')"
                            :value="entry.name"
                            @input="updateField(entry.id, 'name', $event)"
                        />
                        <p v-else class="block mb-4 first-letter:uppercase font-semibold text-black">
                            {{ entry.name }}
                        </p>
                    </div>

                    <div class="flex items-center group">
                        <div class="flex items-start flex-grow space-x-10">
                            <copy-to-clipboard
                                class="grow"
                                width-fit
                                :class="{'hyphens-auto break-all': entry.name === $t('applications.email') || entry.id}"
                                :copy-value="[entry.value, entry.name]"
                                :disabled="!entry.value"
                            >
                                <div
                                    @mousedown.stop
                                    v-if="entry.id" class="grow flex items-center gap-8 relative"
                                >
                                    <div v-if="entry.visibility === 'me'" class="flex items-end">
                                        <tooltip :content="$t('wysiwyg.visibilityOptions.me')">
                                            <svg-icon class="text-semantic-info" type="lock" />
                                        </tooltip>
                                    </div>
                                    <click-edit-input
                                        class="w-full pointer-events-auto"
                                        classes="text-primary"
                                        :disabled="!canManage"
                                        full-width
                                        :placeholder="$t('jobs.description')"
                                        :value="entry.value"
                                        @input="updateField(entry.id, 'value', $event)"
                                        @start-edit="editingField = entry.id"
                                    />
                                    <div
                                        class="h-full flex items-start gap-8 absolute top-0 right-0 pl-12 bg-gradient-to-r from-transparent to-primary-5 to-20%"
                                        v-show="(actionButtonsInfo === entry.name + entry.id || visibleForInfo === entry.id) && editingField !== entry.id"
                                    >
                                        <simple-dropdown
                                            class="flex items-center"
                                            position="right"
                                            on-top
                                            @closed="(visibleForInfo === entry.id ? visibleForInfo = null : '')"
                                            @opened="visibleForInfo = entry.id"
                                            without-chevron
                                        >
                                            <template #trigger>
                                                <svg-icon class="text-primary-60 hover:text-primary" type="visibility" />
                                            </template>
                                            <template #content>
                                                <div v-for="(option, i) in visibilityOptions(entry)" :key="option.key"
                                                     :class="[option.key === entry.visibility ? 'bg-secondary-5 border-secondary z-10' : 'border-primary-20', i === 0 ? 'rounded-t' : '', i === visibilityOptions(entry).length - 1 ? 'rounded-b' : '']"
                                                     class="relative border p-16 flex cursor-pointer"
                                                     @click="option.fn(entry.id)">
                                                    <radio-button
                                                        v-model="entry.visibility"
                                                        :value="option.key"
                                                    />
                                                    <div class="ml-8 flex flex-col cursor-pointer">
                                                        <span
                                                            :class="option.key === entry.visibility ? 'text-secondary' : 'text-primary-80'"
                                                              class="block leading-5"
                                                        >
                                                            {{ option.title }}
                                                        </span>
                                                        <span
                                                            v-if="option.key !== 'selected'"
                                                            :class="option.key === entry.visibility ? 'text-secondary-80' : 'text-primary-80'"
                                                            class="block text-body-sm leading-5 mt-2"
                                                        >
                                                            {{ option.description }}
                                                        </span>
                                                        <div v-else class="mt-8 w-240">
                                                            <user-avatars size="small" :users="getUserModels(entry.users)" left/>
                                                        </div>
                                                    </div>
                                                </div>
                                            </template>
                                        </simple-dropdown>
                                        <button role="button" @click="deleteField(entry.id)">
                                            <svg-icon
                                                class="text-primary-60 hover:text-semantic-error cursor-pointer"
                                                type="delete"
                                            />
                                        </button>
                                    </div>
                                </div>
                                <span v-else>
                                    {{ entry.value }}
                                </span>
                            </copy-to-clipboard>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div v-if="canManage" class="mt-16">
            <simple-button
                :disabled="addingField"
                @click="addField"
                icon="add-circle"
                icon-position="left"
                small
            >
                {{ $t('general.add_new_item') }}
            </simple-button>
        </div>

        <popup-modal v-if="showUserSelectionFor" @close="showUserSelectionFor = false">
            <p class="font-semibold text-body mb-16 text-center">
                {{ $t('wysiwyg.user_selection_title') }}
            </p>
            <div class="-space-y-1">
                <checkbox v-for="(user, i) in users" :key="user.id" v-model="selectedUserIds" :disabled="getField(showUserSelectionFor).user_id === user.id"
                          :label-class="`p-8 border -space-y-px relative ${selectedUserIds.includes(user.id) ? 'bg-secondary-5 border-secondary z-10' : 'border-primary-20 z-0'} ${i === 0 ? 'rounded-t' : ''} ${i === users.length - 1 ? 'rounded-b' : ''}`"
                          :value="user.id" slot-before span-class="flex-grow">
                    <div class="flex items-center">
                        <avatar :image="user.image_url" size="small" class="mr-8">
                            {{ user.initials }}
                        </avatar>
                        <p>{{ user.name }}</p>
                    </div>
                </checkbox>
            </div>

            <simple-button class="mt-16 ml-auto" display="primary" @click="saveSelectedUsers">
                {{ $t('wysiwyg.user_selection_button') }}
            </simple-button>
        </popup-modal>
    </div>
</template>

<script>
import {normalizeJson} from '../../shared/helpers';
import moment from 'moment';
import ApplicationField from '../models/ApplicationField';
import User from '../models/User';
import * as applicationFields from '../services/ApplicationFieldService';
import ClickEditInput from './forms/ClickEditInput.vue';
import SimpleDropdown from './ui/SimpleDropdown.vue';
import UserAvatars from './ui/UserAvatars.vue';
import Avatar from './ui/Avatar.vue';
import PopupModal from './ui/PopupModal.vue';
import Checkbox from './forms/Checkbox.vue';
import SimpleButton from './ui/SimpleButton.vue';
import SvgIcon from './ui/SvgIcon.vue';
import ApplicationButton from "./ui/ApplicationButton.vue";
import CopyToClipboard from "./ui/CopyToClipboard.vue";
import RadioButton from "./forms/RadioButton.vue";
import ApplicationTitle from "./applications/ApplicationTitle.vue";
import Tooltip from "./navigation/Tooltip.vue";
import {mapStores} from "pinia";
import {useMainStore} from "../state/store";
import {useUsersStore} from "../state/modules/users";
import {useRepo} from "pinia-orm";

export default {
    name: 'ApplicationPersonalInfo',
    components: {
        Tooltip,
        ApplicationTitle,
        RadioButton,
        CopyToClipboard,
        ApplicationButton,
        SimpleButton, Checkbox, PopupModal, UserAvatars, Avatar, SimpleDropdown, ClickEditInput, SvgIcon
    },
    props: {
        application: Object,
        canManage: Boolean,
    },
    data: function () {
        return {
            selectedUserIds: [],
            showUserSelectionFor: null,
            actionButtonsInfo: null,
            visibleForInfo: null,
            addingField: false,
            editingField: false,
        };
    },
    computed: {
        ...mapStores(useMainStore, useUsersStore),
        locale() {
            return this.mainStore.locale;
        },
        currentUserId() {
            return this.mainStore.currentUser.id;
        },
        dateFormat() {
            return this.mainStore.dateFormat;
        },
        users() {
            return this.usersModuleStore.allUsersExceptMe;
        },
        personalData() {
            return [
                ...(({
                     email,
                     phone_number,
                     birthdate,
                     birthplace,
                     country,
                     city,
                     postal_code,
                     street,
                     house_number,
                     rijksregister_nr,
                     iban_nr,
                     salary_expectation,
                 }) => ([
                    {name: 'email', value: email},
                    {name: 'phone', value: phone_number},
                    {name: 'birthdate', value: this.formatBirthDate(birthdate)},
                    {name: 'birthplace', value: birthplace},
                    {name: 'address', value: this.formatAddress()},
                    {name: 'ssn', value: rijksregister_nr},
                    {name: 'iban', value: iban_nr},
                    {name: 'salary_expectation', value: salary_expectation},
                ]))(this.application).filter(entry => !!entry.value).map(entry => {
                    entry.name = this.$t('applications.' + entry.name);
                    return entry;
                }),
                ...this.application.application_fields.sort((a, b) => moment(a.created_at).unix() - moment(b.created_at).unix()),
            ];
        },
    },
    methods: {
        formatBirthDate(date) {
            return date ? moment(date).locale(this.locale).format(this.dateFormat) : '';
        },
        formatAddress() {
            let result = [
                [this.application.street, this.application.house_number].filter(Boolean).join(' '),
            ];

            if (this.application.country) {
                result.push([this.application.postal_code, this.application.city, '(' + this.$t('countries.' + this.application.country) + ')'].filter(Boolean).join(' '));
            } else {
                result.push([this.application.postal_code, this.application.city].filter(Boolean).join(' '));
            }

            return result.filter(Boolean).join(', ');
        },
        addField() {
            this.addingField = true;

            applicationFields.store(this.application.id, {
                data: {
                    relationships: {
                        application: {
                            data: {
                                id: this.application.id,
                            },
                        },
                    },
                },
            }).then(data => {
                return useRepo(ApplicationField).save(normalizeJson(data));
            }).then(() => {
                this.addingField = false;
            });
        },
        updateField(fieldId, key, value) {
            let update = {[key]: value};
            if (key === 'visibility' && value === 'selected' && !this.selectedUserIds.includes(this.currentUserId)) {
                // If we're changing visibility, we have to make sure this user is part of the selected list.
                this.selectedUserIds.push(this.currentUserId);
                update['users'] = this.selectedUserIds;
            }

            useRepo(ApplicationField)
                .whereId(fieldId)
                .update(update);

            applicationFields.update(fieldId, {
                data: {
                    attributes: update,
                },
            }).then(() => {
                this.editingField = false;
            })
        },
        deleteField(fieldId) {
            useRepo(ApplicationField).destroy(fieldId);

            applicationFields.destroy(fieldId);
        },
        visibilityOptions(field) {
            return [
                {
                    key: 'all',
                    title: this.$t('wysiwyg.visibilityOptions.all'),
                    description: this.$t('wysiwyg.visibilityOptions.all_desc'),
                    fn: (fieldId) => this.updateField(fieldId, 'visibility', 'all'),
                },
                {
                    key: 'selected',
                    title: this.$t('wysiwyg.visibilityOptions.selected'),
                    description: this.$t('wysiwyg.visibilityOptions.selected_desc'),
                    fn: (fieldId) => {
                        this.showUserSelectionFor = fieldId;
                        this.selectedUserIds = this.selectedUsersForField(fieldId);
                        this.updateField(fieldId, 'visibility', 'selected');
                    },
                },
                ...field.user_id === this.currentUserId ? [{
                    key: 'me',
                    title: this.$t('wysiwyg.visibilityOptions.me'),
                    description: this.$t('wysiwyg.visibilityOptions.me_desc'),
                    fn: (fieldId) => this.updateField(fieldId, 'visibility', 'me'),
                }] : [],
            ];
        },
        selectedUsersForField(fieldId) {
            if (!this.showUserSelectionFor) {
                return [];
            }

            return this.getField(fieldId)?.users || [];
        },
        saveSelectedUsers() {
            this.updateField(this.showUserSelectionFor, 'users', this.selectedUserIds);

            this.showUserSelectionFor = false;
        },
        getUserModels(userIds) {
            return useRepo(User).whereIn('id', userIds).get();
        },
        getField(fieldId) {
            return this.application.application_fields.find(i => i.id === fieldId);
        },
    },
};
</script>
